import HttpStatus from 'http-status-codes'
import lodash from 'lodash'
import HTTP from '@/utils/http'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import { getPrimaryEmail } from '../users/util'

export async function getAccountsAdmins (query = {}) {
  try {
    const response = await HTTP.get('accounts/admins', {
      params: query,
    })
    const result = response.data || {}
    return [result.data, result.count]
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getAllContractorAccounts (query = {}) {
  return getAllAccounts({ ...query, type: 'C' })
}

export async function getAllTenantAccounts (query = {}) {
  return getAllAccounts({ ...query, type: 'T' })
}

export async function getAllAccounts (query = {}) {
  try {
    const response = await HTTP.get('accounts', {
      params: query,
    })
    const result = response.data || {}
    return [result.data, result.count]
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getAdminAccountById (accountId) {
  try {
    const response = await HTTP.get(`accounts/admins/${accountId}`)
    const account = response.data.data
    return account
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

/**
 * Get a company vendor accounts or a vendor account by userId
 *
 * @export
 * @async
 * @param {string} accountId this is company id
 * @param {string} userId filter a user by user id
 * @returns {Object} return a user list
 */
export async function getAccountById (accountId, userId) {
  try {
    if (!accountId) {
      return null
    }
    const response = await HTTP.get(`accounts/${accountId}`)
    const account = response.data.data || {}
    const users = deep(account, 'users.length') && account.users.filter(user => user.id === userId)
    if (users && users.length) {
      const user = users[0]
      user.ccdContacts = user.ccdContacts && lodash.orderBy(user.ccdContacts, 'createdAt').reverse()
      users[0] = user
      account.users = users
    }
    return account
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function handleSpecialName (companyId, type, isSpecial) {
  try {
    const response = await HTTP.put(`/accounts/${companyId}`, { type, handleCompanyName: { isSpecial } })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'error.specialCompany')
    throw err
  }
}

export async function updateAccountAdminDetail (accountId, accountDetail, action) {
  try {
    const response = await HTTP.put(`/accounts/admins/${accountId}`, accountDetail, { headers: { 'X-CMS-Action': action } })
    return response.data.data
  } catch (err) {
    if (err.response.status === HttpStatus.CONFLICT) {
      notification.fireNotification(false, i18n.t('error.editContractor.409'))
    } else {
      notification.handleResponseError(err)
    }
    throw err
  }
}

export async function createAccount (accountInfo, showErrNotification = true) {
  try {
    const response = await HTTP.post('/accounts/', accountInfo)
    return response
  } catch (err) {
    if (showErrNotification) {
      if (err.response.status === HttpStatus.CONFLICT) {
        notification.fireNotification(false, i18n.t('error.subAccount.409'))
      } else {
        notification.handleResponseError(err)
      }
    }
    throw err
  }
}

export async function updateSubAccountDetail (accountId, accountDetails = []) {
  try {
    const response = await HTTP.put(`/accounts/admins/${accountId}/subs`, accountDetails)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    if (err.response.status === HttpStatus.CONFLICT) {
      notification.fireNotification(false, i18n.t('error.editContractor.409'))
    } else {
      notification.handleResponseError(err)
    }
    throw err
  }
}

export async function findSubAccounts (accountId) {
  try {
    const response = await HTTP.get(`/accounts/admins/${accountId}/subs`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function findAllSubAccounts (accountId) {
  try {
    const response = await HTTP.get(`/accounts/admins/${accountId}/all_subs`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function inviteAccount (id, request, notifyPrefix) {
  try {
    const response = await HTTP.post(`/accounts/invite/${id}`, request)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, notifyPrefix)
    throw err
  }
}

export async function inviteAccounts (request, notifyPrefix) {
  try {
    const response = await HTTP.post(`/accounts/invite`, request)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, notifyPrefix)
    throw err
  }
}

export async function getEngineersPrimaryEmails (ids) {
  try {
    const response = await HTTP.get(`accounts/engineers?ids=${ids}`)
    const result = response.data.data
    const emails = result.map(user => getPrimaryEmail(user))
    return emails
  } catch (err) {
    throw err
  }
}

export async function getLockedUsers () {
  try {
    const response = await HTTP.get('/accounts/lock_users')
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function unlockAccounts (userList) {
  try {
    const response = await HTTP.put(`/accounts/unlock_users`, userList)
    notification.fireNotification(true, i18n.t('success'))
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)

    throw err
  }
}

/**
 * Checks if a user is a project representative for a given company.
 *
 * @param {string|number} companyId - The ID of the company to check against.
 * @param {string} username - The username of the user to check.
 * @param {boolean} isAdmin - Whether the user for a vendor master/sub.
 * @returns {Promise<boolean>} Returns false if the user is a project representative, true if not, or throws an error.
 */
export async function checkIfProjectRep (companyId, username, isAdmin) {
  try {
    await HTTP.get(`/accounts/${companyId}/project_rep_checking`, { params: { username, isAdmin } })
    return false
  } catch (err) {
    const errMsg = lodash.get(err, 'response.data.message')
    if (errMsg) {
      console.error(err)
      notification.error(errMsg)
      return true
    }
    notification.handleResponseError(err)
    throw err
  }
}
